import {ChartData, ChartDataset, ChartOptions,} from 'chart.js';
import 'chartjs-adapter-date-fns';
import {useEffect, useState} from "react";
import {Chart, ChartProps} from '../Chart/Chart';
import {LayoutPosition} from "chart.js/dist/types/layout";

export type BarChartBucketData = Omit<ChartDataset<'bar'>, 'label' | 'data'> & {
  stacked?: BarChartStackProps
  values: (number | [number, number] | null)[]
}

export type BarChartData = {
  titles: string[]
  buckets: Record<string, BarChartBucketData>
}

export type BarChartStackProps = {
  enabled?: boolean
}

export type BarChartProps = Omit<ChartProps<'bar'>, 'type' | 'options' | 'data'> & {
  stacked?: BarChartStackProps
  xAxisTitle?: string
  yAxisTitle?: string
  tooltipPos?: LayoutPosition
  data?: BarChartData
};

export const BarChart = (props: BarChartProps) => {
  const {
    stacked,
    xAxisTitle,
    yAxisTitle,
    tooltipPos,
    data,
    ...rest
  } = props;

  const [chartOptions, setChartOptions] = useState<
    ChartOptions<'bar'> | undefined
  >(undefined);

  const [chartData, setChartData] = useState<
    ChartData<'bar'> | undefined
  >(undefined);

  useEffect(() => {
    setChartOptions(getChartOptions(stacked, xAxisTitle, yAxisTitle, tooltipPos))
  }, [stacked, tooltipPos, xAxisTitle, yAxisTitle])

  useEffect(() => {
    setChartData(getChartData(data))
  }, [data])

  return <Chart {...rest} type="bar" options={chartOptions} data={chartData}/>
};


const getChartOptions = (stacked: BarChartStackProps | undefined, xAxisTitle: string | undefined, yAxisTitle: string | undefined, tooltipPos: LayoutPosition | undefined): ChartOptions<'bar'> => {
  return {
    maintainAspectRatio: true,
    animation: false,
    responsive: true,
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        stacked: stacked?.enabled,
        title: {
          display: true,
          text: xAxisTitle,
        },
      },
      y: {
        stacked: stacked?.enabled,
        beginAtZero: true,
        title: {
          display: true,
          text: yAxisTitle
        },
      }
    },
    plugins: {
      legend: {
        position: tooltipPos ?? 'top',
      },
      datalabels: {
        display: false
      }
    }
  };
};

const getChartData = (data: BarChartData | undefined): ChartData<'bar'> => {
  const chartDataSets: ChartDataset<'bar'>[] = [];

  if (data) {
    for (const key of Object.keys(data.buckets)) {
      const bucket = data.buckets[key]

      chartDataSets.push({
        ...bucket,
        label: key,
        data: bucket.values,
      });
    }
  }

  return {
    labels: data?.titles,
    datasets: chartDataSets,
  };
};
